import {
  MUTATION_RESPONSE_RECEIVED,
  QUERY_RESPONSE_RECEIVED,
} from '@web-platform/shared-universal-constants/src/network';
import * as persistanceKeys from '@web-platform/shared-universal-constants/src/persistanceKeys';
import { SEARCH_PARAMS_CHANGED } from '@web-platform/shared-universal-constants/src/userSearch';
import {
  RADAR_CREATE_SESSIONS,
  RADAR_POLL,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { isResponseContainFlightsBody } from '@web-platform/shared-universal-utils-frontend/src/helpers/radar/flightsResponse';
import {
  getRadarFlightsResponseBody,
  isRadarFlightsResponseError,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/radar/radarFlightsResponse';
import { tryGetValue } from '@web-platform/shared-universal-utils-frontend/src/helpers/sessionStorage';

import { FILTERS_RESET, STOPS_FILTER_CHANGED } from '../../../../../../actions/filters';

import { mapStops } from './stopsMapper';

import type { Action } from '@web-platform/shared-universal-types/src/redux/banana/actions/actions-banana-types';
import type { FiltersState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/state';
import type { StopsFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/stops';

export const INITIAL_STATE = {
  direct: {
    disabled: true,
    value: null,
    price: null,
  },
  oneStop: {
    disabled: true,
    value: null,
    price: null,
  },
  twoPlusStops: {
    disabled: true,
    value: null,
    price: null,
  },
};

// @ts-expect-error FiltersState type is not compatible with empty object
const filtersInSession = (): FiltersState => tryGetValue(persistanceKeys.FILTERS, {});

const storedStateFilters = () => {
  const filters = filtersInSession();
  return filters?.stops && Object.keys(filters.stops).length ? filters.stops : INITIAL_STATE;
};

export const createStopsReducer =
  (getInitialState: () => StopsFilterState = storedStateFilters) =>
  (state: StopsFilterState = getInitialState(), action: Action): StopsFilterState => {
    switch (action.type) {
      case MUTATION_RESPONSE_RECEIVED:
      case QUERY_RESPONSE_RECEIVED: {
        switch (action.payload.type) {
          case RADAR_CREATE_SESSIONS:
          case RADAR_POLL: {
            if (
              isRadarFlightsResponseError(action.payload.response) ||
              !isResponseContainFlightsBody(action.payload.response)
            ) {
              return state;
            }

            const flightsResponseBody = getRadarFlightsResponseBody(action.payload.response);
            const stats = mapStops(flightsResponseBody.filterStats);
            if (!stats) return state;
            const {
              filter_hints: filtersEnabled = {
                enable_direct_filter: false,
                enable_one_stop_filter: false,
                enable_two_plus_stops_filter: false,
              },
              itineraries,
            } = stats;

            const {
              direct,
              one_stop: oneStop,
              two_plus_stops: twoPlusStops,
            } = itineraries.stops ?? {};

            return {
              direct: {
                ...state.direct,
                disabled: !filtersEnabled.enable_direct_filter,
                price: direct?.total?.min_price || 0,
              },
              oneStop: {
                ...state.oneStop,
                disabled: !filtersEnabled.enable_one_stop_filter,
                price: oneStop?.total?.min_price || 0,
              },
              twoPlusStops: {
                ...state.twoPlusStops,
                disabled: !filtersEnabled.enable_two_plus_stops_filter,
                price: twoPlusStops?.total?.min_price || 0,
              },
            };
          }

          default: {
            return state;
          }
        }
      }

      case STOPS_FILTER_CHANGED:
        return {
          direct: { ...state.direct, value: action.payload.direct },
          oneStop: { ...state.oneStop, value: action.payload.oneStop },
          twoPlusStops: { ...state.twoPlusStops, value: action.payload.twoPlusStops },
        };

      case FILTERS_RESET:
        return {
          direct: { ...state.direct, value: true },
          oneStop: { ...state.oneStop, value: true },
          twoPlusStops: { ...state.twoPlusStops, value: true },
        };

      case SEARCH_PARAMS_CHANGED:
        return state;

      default:
        return state;
    }
  };

export default createStopsReducer();
export const get = (state: StopsFilterState): StopsFilterState => state;
